import React, { useEffect, useState, useRef } from 'react';
import { ArrowRightIcon, CheckCircleIcon, TrendingUpIcon, ZapIcon, MessageSquareIcon, SendIcon, XIcon, SparklesIcon, ExternalLinkIcon, CalendarIcon } from 'lucide-react';
const Home2Page = () => {
  const [messages, setMessages] = useState<Array<{
    text: string;
    isUser: boolean;
    hasLink?: boolean;
  }>>([{
    text: "Hi! 👋 I'm ShoPilots AI. I can answer any questions about our platform, pricing, features, or help you get started. What would you like to know?",
    isUser: false
  }]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  // Comprehensive knowledge base
  const getAIResponse = (userInput: string): string => {
    const input = userInput.toLowerCase();
    // Intent detection for signup
    const signupIntents = ['sign up', 'signup', 'register', 'create account', 'get started', 'start trial', 'try it', 'free trial', 'begin', 'start now'];
    if (signupIntents.some(intent => input.includes(intent))) {
      return "Perfect! I'll direct you to our signup page where you can create your free account in just 2 minutes. No credit card required for the 30-day trial.\n\n👉 Click here to start: https://chat.shopilots.com/signup?product=shopilots";
    }
    // Pricing questions
    if (input.includes('price') || input.includes('cost') || input.includes('pricing') || input.includes('how much')) {
      return 'We offer transparent, simple pricing:\n\n• Starter: $39/month (1 AI agent, 250 conversations)\n• Professional: $99/month (2 AI agents, 2,000 conversations)\n• Enterprise: Custom pricing\n\nAll plans include a 30-day free trial with no credit card required. Want to start your trial?';
    }
    // Features questions
    if (input.includes('feature') || input.includes('what can') || input.includes('capabilities') || input.includes('what do')) {
      return 'ShoPilots AI can:\n\n✓ Answer product questions 24/7\n✓ Recommend products based on customer needs\n✓ Handle order tracking and returns\n✓ Recover abandoned carts\n✓ Provide multi-language support\n✓ Deploy on website, WhatsApp, Instagram, Telegram\n\nWant to see it in action? Start your free trial!';
    }
    // Integration questions
    if (input.includes('integrat') || input.includes('platform') || input.includes('shopify') || input.includes('woocommerce')) {
      return 'We integrate seamlessly with:\n\n• Shopify, WooCommerce, Magento\n• WhatsApp, Facebook Messenger, Instagram\n• Telegram, SMS\n• Zendesk, Freshdesk\n• Google Analytics\n\nSetup takes just 5 minutes. Ready to connect your store?';
    }
    // Setup/implementation questions
    if (input.includes('setup') || input.includes('install') || input.includes('implement') || input.includes('how to start')) {
      return "Getting started is super easy:\n\n1. Sign up (2 minutes)\n2. Connect your product catalog\n3. Customize your AI's personality\n4. Deploy to your channels\n\nNo coding required! Most customers are live within 5 minutes. Want to try it?";
    }
    // Results/ROI questions
    if (input.includes('result') || input.includes('roi') || input.includes('conversion') || input.includes('performance')) {
      return 'Our customers see real results:\n\n• +25% average conversion rate increase\n• 90% reduction in support costs\n• 45-second average response time\n• 10M+ conversations handled\n\n1,500+ retailers trust ShoPilots. Ready to join them?';
    }
    // Demo questions
    if (input.includes('demo') || input.includes('see it') || input.includes('show me')) {
      return "I'd love to show you more! You have two options:\n\n1. Start a free 30-day trial and explore on your own\n2. Book a personalized demo with our team\n\nWhich would you prefer?";
    }
    // Support questions
    if (input.includes('support') || input.includes('help') || input.includes('customer service')) {
      return 'We offer comprehensive support:\n\n• 24/7 AI-powered assistance\n• Live chat with our team\n• Detailed documentation\n• Video tutorials\n• Dedicated account manager (Enterprise)\n\nPlus, our AI handles your customer support automatically! Want to see how?';
    }
    // Industry-specific questions
    if (input.includes('ecommerce') || input.includes('retail') || input.includes('fashion') || input.includes('electronics')) {
      return 'ShoPilots is built specifically for retail:\n\n• E-commerce: Product recommendations, cart recovery\n• Fashion: Style advice, size guidance\n• Electronics: Technical specs, comparisons\n• Home & Garden: Project ideas, compatibility\n\nWe understand your industry. Ready to see it in action?';
    }
    // Free trial questions
    if (input.includes('trial') || input.includes('free')) {
      return 'Yes! We offer a 30-day free trial:\n\n✓ Full access to all features\n✓ No credit card required\n✓ Cancel anytime\n✓ Setup in 5 minutes\n\nOver 1,500 retailers have started with our free trial. Ready to join them?\n\n👉 Start here: https://chat.shopilots.com/signup?product=shopilots';
    }
    // Comparison questions
    if (input.includes('vs') || input.includes('compare') || input.includes('better than') || input.includes('intercom') || input.includes('drift')) {
      return 'ShoPilots vs others:\n\n✓ Built specifically for e-commerce (not generic)\n✓ 70% cheaper than Intercom\n✓ No per-resolution fees\n✓ Setup in minutes (not weeks)\n✓ +25% proven conversion increase\n\nWant to see the full comparison or try it yourself?';
    }
    // Product questions (simulate shopping)
    if (input.includes('product') || input.includes('jacket') || input.includes('winter') || input.includes('buy') || input.includes('looking for')) {
      return 'I can help you find the perfect product! In a live deployment, I would:\n\n• Ask about your preferences and budget\n• Show relevant products from your catalog\n• Answer questions about sizing, materials, shipping\n• Add items to cart and complete checkout\n\nThis is just a demo. Want to see the full AI in action with your products? Start your free trial!';
    }
    // Language/multilingual questions
    if (input.includes('language') || input.includes('multilingual') || input.includes('translation')) {
      return 'Yes! ShoPilots supports multiple languages:\n\n• Automatic language detection\n• 50+ languages supported\n• Maintains your brand voice in each language\n• No extra setup required\n\nPerfect for international retailers. Want to try it?';
    }
    // Security/privacy questions
    if (input.includes('security') || input.includes('privacy') || input.includes('data') || input.includes('gdpr')) {
      return "Security is our priority:\n\n✓ SOC 2 Type II certified\n✓ GDPR compliant\n✓ End-to-end encryption\n✓ Your data stays yours\n✓ No data sharing with third parties\n\nYour customers' information is safe with us. Ready to get started?";
    }
    // Greeting responses
    if (input.includes('hi') || input.includes('hello') || input.includes('hey')) {
      return "Hi there! 👋 I'm ShoPilots AI. I can answer questions about:\n\n• Features & capabilities\n• Pricing & plans\n• Integration & setup\n• Results & ROI\n\nWhat would you like to know?";
    }
    // Thank you responses
    if (input.includes('thank') || input.includes('thanks')) {
      return "You're welcome! 😊 Is there anything else you'd like to know? Or are you ready to start your free trial?";
    }
    // Default intelligent response
    return 'Great question! ShoPilots is an AI shopping assistant that helps retailers:\n\n• Increase conversions by 25%\n• Provide 24/7 customer support\n• Deploy across all channels (web, WhatsApp, Instagram, etc.)\n• Set up in just 5 minutes\n\nWe have 1,500+ happy retailers and handle 10M+ conversations monthly. Want to try it free for 30 days?\n\n👉 Start here: https://chat.shopilots.com/signup?product=shopilots';
  };
  const handleSendMessage = () => {
    if (!inputValue.trim()) return;
    const userMessage = inputValue.trim();
    setMessages(prev => [...prev, {
      text: userMessage,
      isUser: true
    }]);
    setInputValue('');
    setIsTyping(true);
    setTimeout(() => {
      const response = getAIResponse(userMessage);
      const hasLink = response.includes('https://');
      setMessages(prev => [...prev, {
        text: response,
        isUser: false,
        hasLink
      }]);
      setIsTyping(false);
    }, 1200);
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  const handleQuickPrompt = (prompt: string) => {
    setInputValue(prompt);
    setTimeout(() => handleSendMessage(), 100);
  };
  return <div className="min-h-screen bg-white" data-id="element-3187">
      {/* Ultra-clean Hero Section */}
      <section className="relative min-h-[90vh] flex items-center" data-id="element-3188">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-id="element-3189">
          <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-3190">
            {/* Left: Minimal value prop */}
            <div className="max-w-xl" data-id="element-3191">
              <div className="inline-flex items-center gap-2 bg-purple-100 rounded-full px-4 py-2 mb-8" data-id="element-3192">
                <SparklesIcon className="h-4 w-4 text-purple-700" data-id="element-3193" />
                <span className="text-sm font-medium text-purple-700" data-id="element-3194">
                  AI-First Commerce
                </span>
              </div>

              <h1 className="text-6xl md:text-7xl font-bold text-gray-900 mb-6 leading-[1.1] tracking-tight" data-id="element-3195">
                Turn conversations into sales
              </h1>

              <p className="text-xl text-gray-600 mb-10 leading-relaxed" data-id="element-3196">
                AI shopping assistants that increase conversions by 25%. Deploy
                in 5 minutes.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8" data-id="element-3197">
                <a href="https://chat.shopilots.com/signup?product=shopilots" className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 whitespace-nowrap" data-id="element-3198">
                  Start free trial
                  <ArrowRightIcon className="h-5 w-5" data-id="element-3199" />
                </a>
                <a href="https://calendar.app.google/7twCNBfwZBuVCKbk7" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-900 text-lg font-medium rounded-lg border-2 border-gray-200 hover:border-orange-500 transition-colors" data-id="element-3200">
                  <CalendarIcon className="h-5 w-5 mr-2" data-id="element-3201" />
                  Book a demo
                </a>
              </div>

              <p className="text-sm text-gray-500" data-id="element-3202">
                Free 30-day trial · No credit card · 1,500+ retailers
              </p>
            </div>

            {/* Right: Always-open chat widget */}
            <div className="relative" data-id="element-3203">
              <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden" data-id="element-3204">
                <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4 flex items-center justify-between" data-id="element-3205">
                  <div className="flex items-center gap-3" data-id="element-3206">
                    <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center" data-id="element-3207">
                      <SparklesIcon className="h-5 w-5 text-orange-600" data-id="element-3208" />
                    </div>
                    <div data-id="element-3209">
                      <div className="font-semibold text-white" data-id="element-3210">
                        ShoPilots AI
                      </div>
                      <div className="text-xs text-orange-100 flex items-center gap-1" data-id="element-3211">
                        <div className="w-2 h-2 bg-green-400 rounded-full" data-id="element-3212"></div>
                        Online
                      </div>
                    </div>
                  </div>
                </div>

                <div className="h-96 overflow-y-auto p-6 space-y-4 bg-gray-50" data-id="element-3213">
                  {messages.map((message, index) => <div key={index} className={`flex ${message.isUser ? 'justify-end' : 'justify-start'}`} data-id="element-3214">
                      <div className={`max-w-xs px-4 py-3 rounded-2xl ${message.isUser ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-tr-sm' : 'bg-white text-gray-900 rounded-tl-sm border border-gray-200'}`} data-id="element-3215">
                        <p className="text-sm whitespace-pre-line" data-id="element-3216">
                          {message.text.split('https://').map((part, i) => {
                        if (i === 0) return part;
                        const [url, ...rest] = part.split(/\s/);
                        return <span key={i} data-id="element-3217">
                                <a href={`https://${url}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-purple-600 hover:text-purple-700 underline font-medium" data-id="element-3218">
                                  Start your free trial
                                  <ExternalLinkIcon className="h-3 w-3" data-id="element-3219" />
                                </a>
                                {rest.length > 0 && ' ' + rest.join(' ')}
                              </span>;
                      })}
                        </p>
                      </div>
                    </div>)}
                  {isTyping && <div className="flex justify-start" data-id="element-3220">
                      <div className="bg-white px-4 py-3 rounded-2xl rounded-tl-sm border border-gray-200" data-id="element-3221">
                        <div className="flex gap-1" data-id="element-3222">
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" data-id="element-3223"></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{
                        animationDelay: '0.1s'
                      }} data-id="element-3224"></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{
                        animationDelay: '0.2s'
                      }} data-id="element-3225"></div>
                        </div>
                      </div>
                    </div>}
                  <div ref={messagesEndRef} data-id="element-3226" />
                </div>

                <div className="p-4 bg-white border-t border-gray-200" data-id="element-3227">
                  <div className="flex gap-2" data-id="element-3228">
                    <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyPress={handleKeyPress} placeholder="Ask me anything..." className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 transition-colors text-sm" data-id="element-3229" />
                    <button onClick={handleSendMessage} disabled={!inputValue.trim()} className="px-4 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl hover:from-orange-600 hover:to-orange-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3230">
                      <SendIcon className="h-5 w-5" data-id="element-3231" />
                    </button>
                  </div>
                  <div className="mt-3 flex gap-2 flex-wrap" data-id="element-3232">
                    <button onClick={() => handleQuickPrompt('What are your pricing plans?')} className="text-xs px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors" data-id="element-3233">
                      Pricing
                    </button>
                    <button onClick={() => handleQuickPrompt('What features do you offer?')} className="text-xs px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors" data-id="element-3234">
                      Features
                    </button>
                    <button onClick={() => handleQuickPrompt('I want to start a free trial')} className="text-xs px-3 py-1.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-full hover:from-orange-600 hover:to-orange-700 transition-all" data-id="element-3235">
                      Start trial
                    </button>
                  </div>
                </div>
              </div>

              {/* Floating metric */}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-4 border border-gray-200" data-id="element-3236">
                <div className="text-2xl font-bold text-orange-600" data-id="element-3237">+25%</div>
                <div className="text-xs text-gray-600" data-id="element-3238">Conversion</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Minimal stats */}
      <section className="py-16 border-y border-gray-200" data-id="element-3239">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3240">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center" data-id="element-3241">
            <div data-id="element-3242">
              <div className="text-4xl font-bold text-orange-600 mb-1" data-id="element-3243">
                +25%
              </div>
              <div className="text-sm text-gray-600" data-id="element-3244">Conversion rate</div>
            </div>
            <div data-id="element-3245">
              <div className="text-4xl font-bold text-orange-600 mb-1" data-id="element-3246">90%</div>
              <div className="text-sm text-gray-600" data-id="element-3247">Cost reduction</div>
            </div>
            <div data-id="element-3248">
              <div className="text-4xl font-bold text-orange-600 mb-1" data-id="element-3249">
                10M+
              </div>
              <div className="text-sm text-gray-600" data-id="element-3250">Conversations</div>
            </div>
            <div data-id="element-3251">
              <div className="text-4xl font-bold text-orange-600 mb-1" data-id="element-3252">
                1,500+
              </div>
              <div className="text-sm text-gray-600" data-id="element-3253">Retailers</div>
            </div>
          </div>
        </div>
      </section>

      {/* Clean features */}
      <section className="py-24" data-id="element-3254">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3255">
          <div className="max-w-2xl mb-16" data-id="element-3256">
            <h2 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-3257">
              Built for modern retail
            </h2>
            <p className="text-xl text-gray-600" data-id="element-3258">
              Everything you need to deliver exceptional customer experiences
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12" data-id="element-3259">
            <div data-id="element-3260">
              <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl flex items-center justify-center mb-6" data-id="element-3261">
                <TrendingUpIcon className="h-6 w-6 text-white" data-id="element-3262" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-3263">
                Increase sales
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-3264">
                AI recommendations that understand intent and guide customers to
                purchase
              </p>
            </div>

            <div data-id="element-3265">
              <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl flex items-center justify-center mb-6" data-id="element-3266">
                <MessageSquareIcon className="h-6 w-6 text-white" data-id="element-3267" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-3268">
                24/7 support
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-3269">
                Instant responses to customer questions at any time of day or
                night
              </p>
            </div>

            <div data-id="element-3270">
              <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl flex items-center justify-center mb-6" data-id="element-3271">
                <ZapIcon className="h-6 w-6 text-white" data-id="element-3272" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-3273">
                Multi-channel
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-3274">
                Deploy on website, WhatsApp, Instagram, and more with one
                platform
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Social proof */}
      <section className="py-16 bg-gray-50" data-id="element-3275">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3276">
          <p className="text-center text-sm font-medium text-gray-500 uppercase tracking-wide mb-8" data-id="element-3277">
            Trusted by leading platforms
          </p>
          <div className="flex flex-wrap justify-center items-center gap-12 opacity-40 grayscale" data-id="element-3278">
            <img src="https://cdn.worldvectorlogo.com/logos/shopify.svg" alt="Shopify" className="h-8" data-id="element-3279" />
            <img src="https://cdn.worldvectorlogo.com/logos/woocommerce.svg" alt="WooCommerce" className="h-8" data-id="element-3280" />
            <img src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" alt="WhatsApp" className="h-8" data-id="element-3281" />
            <img src="https://cdn.worldvectorlogo.com/logos/telegram-1.svg" alt="Telegram" className="h-8" data-id="element-3282" />
          </div>
        </div>
      </section>

      {/* Minimal CTA */}
      <section className="py-24 bg-gradient-to-r from-orange-500 to-orange-600 text-white" data-id="element-3283">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-3284">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-3285">
            Start your free trial
          </h2>
          <p className="text-xl text-orange-100 mb-10" data-id="element-3286">
            No credit card required · Setup in 5 minutes
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-3287">
            <a href="https://chat.shopilots.com/signup?product=shopilots" className="bg-white text-orange-600 px-8 py-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 whitespace-nowrap hover:bg-orange-50" data-id="element-3288">
              Get started
              <ArrowRightIcon className="h-5 w-5" data-id="element-3289" />
            </a>
            <a href="https://calendar.app.google/7twCNBfwZBuVCKbk7" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-10 py-4 bg-transparent text-white text-lg font-medium rounded-lg border-2 border-white hover:bg-white hover:text-orange-600 transition-all" data-id="element-3290">
              <CalendarIcon className="h-5 w-5 mr-2" data-id="element-3291" />
              Book demo
            </a>
          </div>
        </div>
      </section>
    </div>;
};
export default Home2Page;